import { computed, watch } from 'vue'
import { useUserStore } from '../../store/user'
import { useConfigurationStore } from '../../store/configurations'
import { isInventoryConfigEnabled } from '../../modules/inventory/utils/transfersConfig'
import { getUserActiveInventoryLocationId } from '../../modules/inventory/utils/transferLocationContext'
import { useTransferStatisticsStore } from '../../modules/inventory/store'
import type { INavigationRoute } from './NavigationRoutes'

export type SidebarNavBadgeInfo = {
  count: number
  color: string
  label?: string
}

/**
 * Register each submenu's badge here. Parent items never get their own source —
 * they sum every visible descendant that has a count.
 */
const useChildBadgeSources = () => {
  const transferStatisticsStore = useTransferStatisticsStore()

  return computed<Record<string, SidebarNavBadgeInfo>>(() => {
    const incomingTransfers = transferStatisticsStore.incomingCount
    const badges: Record<string, SidebarNavBadgeInfo> = {}

    if (incomingTransfers > 0) {
      badges['inventory-transfers'] = {
        count: incomingTransfers,
        color: 'danger',
        label: `${incomingTransfers} incoming transfers`,
      }
    }

    return badges
  })
}

export const aggregateSidebarBadges = (
  badges: Array<SidebarNavBadgeInfo | null | undefined>,
): SidebarNavBadgeInfo | null => {
  const active = badges.filter((badge): badge is SidebarNavBadgeInfo => Boolean(badge && badge.count > 0))
  if (active.length === 0) {
    return null
  }

  const count = active.reduce((sum, badge) => sum + badge.count, 0)
  const colors = new Set(active.map((badge) => badge.color))

  return {
    count,
    color: colors.size === 1 ? active[0].color : 'danger',
    label: active.length === 1 ? active[0].label : `${count} pending items`,
  }
}

export const resolveSidebarNavBadge = (
  route: INavigationRoute,
  childBadges: Record<string, SidebarNavBadgeInfo>,
  isChildVisible: (child: INavigationRoute) => boolean,
): SidebarNavBadgeInfo | null => {
  const visibleChildren = (route.children ?? []).filter((child) => isChildVisible(child))
  if (visibleChildren.length > 0) {
    return aggregateSidebarBadges(
      visibleChildren.map((child) => resolveSidebarNavBadge(child, childBadges, isChildVisible)),
    )
  }

  return childBadges[route.name] ?? null
}

/**
 * Sidebar badge lookup for any nav item. Add a source in `useChildBadgeSources`
 * when a new submenu has counts; parents pick them up automatically.
 */
export const useSidebarNavBadges = (isChildVisible: (child: INavigationRoute) => boolean) => {
  const userStore = useUserStore()
  const configStore = useConfigurationStore()
  const transferStatisticsStore = useTransferStatisticsStore()
  const childBadges = useChildBadgeSources()

  const canLoadTransferStatistics = computed(
    () =>
      userStore.hasPermission('read', 'stock-transfer') &&
      isInventoryConfigEnabled(configStore.inventoryConfigs, 'enable_transfers'),
  )
  const activeInventoryLocationId = computed(() => getUserActiveInventoryLocationId(userStore.authUser))

  watch(
    [canLoadTransferStatistics, activeInventoryLocationId],
    ([canLoad, locationId]) => {
      if (!canLoad || locationId == null) {
        transferStatisticsStore.reset()
        return
      }
      void transferStatisticsStore.dispatchGetTransferStatistics()
    },
    { immediate: true },
  )

  const navBadge = (route: INavigationRoute): SidebarNavBadgeInfo | null =>
    resolveSidebarNavBadge(route, childBadges.value, isChildVisible)

  const navBadgeProps = (route: INavigationRoute) => {
    const badge = navBadge(route)
    return {
      count: badge?.count ?? 0,
      color: badge?.color,
      ariaLabel: badge?.label,
    }
  }

  return { navBadge, navBadgeProps }
}
